import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators, regexValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { Company } from '@shared/models/data-model';
import { RegistrationHelper } from '@shared/registration-helper';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID, StepService } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'company-info',
  templateUrl: './company-info.component.html',
  styleUrl: './company-info.component.scss'
})
export class CompanyInfoComponent implements OnInit {
    private readonly subStepName = "Company Identifiers";
    private companyToSave: Company;
    loading = false;
    form: UntypedFormGroup;
    market: string;
    skipRegistrationNumber = false;
    isItaCharityOrAsd = false;

    toUppercase = (value: unknown): string => String(value).toUpperCase();

    get canSwitchToSearch(): boolean {
        return this.stepService.canSwitchToCompanySearch();
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly stepService: StepService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const company: Company = this.route.snapshot.data.company;
        this.companyToSave = RegistrationHelper.cloneCompany(company);

        this.market = company?.registeredAddress?.countryISO;
        this.isItaCharityOrAsd = this.market === 'ITA' && ['AssociazioneSportiva', 'OnlusNoProfit'].includes(company?.type);

        const registrationNumberValidators = [];

        if (!this.isItaCharityOrAsd) {
            registrationNumberValidators.push(Validators.required);
        }
        if (this.market === 'ITA') {
            registrationNumberValidators.push(Validators.pattern(regexValidators.rea));
        }
        registrationNumberValidators.push(Validators.maxLength(CustomValidators.getCompanyMaxlength('registrationNumber')));

        this.form = this.fb.group({
            registrationNumber: [company?.registrationNumber, registrationNumberValidators],
            businessName: [company?.registeredName, [Validators.required, Validators.maxLength(CustomValidators.getCompanyMaxlength('registeredName'))]],
            useCodiceFiscale: [Boolean(company?.fiscalCode)]
        });

        this.form.addControl("tradingName", new UntypedFormControl(company?.tradingName, Validators.maxLength(CustomValidators.getCompanyMaxlength('tradingName'))));

        const codiceFiscaleControl = new UntypedFormControl(company?.fiscalCode, [Validators.required, Validators.pattern('[0-9]{11}')]);
        const vatNumberControl = new UntypedFormControl(company?.vatNumber);

        if (this.market === "ITA") {
            
            vatNumberControl.setValidators([Validators.pattern(regexValidators.piva), Validators.required]);
        } else {
            vatNumberControl.setValidators([Validators.maxLength(14)]);
        }

        this.form.get("useCodiceFiscale").valueChanges
            .pipe(startWith(Boolean(company?.fiscalCode)))
            .subscribe(value => {
                if (value && this.isItaCharityOrAsd) {
                    this.form.addControl("codiceFiscale", codiceFiscaleControl);
                    if (vatNumberControl.touched) {
                        codiceFiscaleControl.markAsTouched();
                    }
                    this.removeFormControl("vatNumber");
                } else {
                    this.form.addControl("vatNumber", vatNumberControl);
                    if (codiceFiscaleControl.touched) {
                        vatNumberControl.markAsTouched();
                    }
                    this.removeFormControl("codiceFiscale");
                }
            });
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

    switchToSearch(): void {
        this.amplitudeService.trackCompanyInputModeSwitched(this.market, 'toSearch');
        this.router.navigate([this.registrationService.selectCompanySearchInputMode()]);
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            const formValues = this.form.value;
            this.companyToSave.kybProviderId = null;
            this.companyToSave.registrationNumber = formValues.registrationNumber;
            this.companyToSave.fiscalCode = formValues.codiceFiscale ?? '';
            this.companyToSave.vatNumber = formValues.vatNumber ?? '';
            this.companyToSave.registeredName = formValues.businessName;
            this.companyToSave.tradingName = formValues.tradingName;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveCompany(this.companyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.COMPANY_IDENTIFIERS);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save company failed', e, 'with company', this.companyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

    private removeFormControl(controlName: string) {
        const control = this.form.get(controlName);
        if (!control) {
            return;
        }
        control.setValue("");
        this.form.removeControl(controlName);
    }
}
